import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ExternalLink,
  Copy,
  AlertTriangle,
  Download,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { Project } from '@/types/conversations'
import { csrfHeaders } from '@/lib/csrf'
import { BuyDomainDialog } from './BuyDomainDialog'
import { AddByodDialog } from './AddByodDialog'

// Strip the zone apex from a DNS record name for display.
// DNS providers auto-append the zone apex (e.g., premiumprinting.io), so we
// strip that — NOT the full SAN domain. For www SANs like
// _hash.www.premiumprinting.io the display should be _hash.www, not just _hash.
function stripDomainSuffix(name: string, domain: string): string {
  // Find the zone apex: strip leading subdomain labels (e.g., www.)
  const MULTI_LEVEL_TLDS = ['co.uk','com.au','org.uk','net.au','co.nz','com.br','co.jp','org.au','co.in','com.sg','co.za','com.mx','co.kr','com.hk','com.tw']
  const parts = domain.split('.')
  const suffix = parts.slice(-2).join('.')
  const isMultiTld = MULTI_LEVEL_TLDS.includes(suffix)
  const apexParts = isMultiTld ? 3 : 2
  const zone = parts.slice(-apexParts).join('.')

  const zoneSuffix = '.' + zone
  return name.endsWith(zoneSuffix) ? name.slice(0, -zoneSuffix.length) : name
}

function CopyableValue({ value, t }: { value: string; t: (key: string) => string }) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      <code className="font-mono text-amber-900 dark:text-amber-300 break-all text-xs">{value}</code>
      <Button
        size="sm"
        variant="ghost"
        className="h-5 w-5 p-0 flex-shrink-0"
        onClick={() => {
          navigator.clipboard.writeText(value)
          toast.success(t('domain.toasts.copiedToClipboard'))
        }}
      >
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  )
}

function DnsRecordRow({ type, name, value, copyName, fullName, t }: {
  type: string
  name: string
  value: string
  copyName?: boolean
  fullName?: string
  t: (key: string) => string
}) {
  const [showFullName, setShowFullName] = useState(false)

  const nameDisplay = copyName
    ? <CopyableValue value={name} t={t} />
    : <code className="font-mono text-amber-900 dark:text-amber-300 break-all">{name}</code>

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-amber-700 dark:text-amber-400 font-medium">{t('domain.byod.recordType')}:</span>
        <code className="font-mono text-amber-900 dark:text-amber-300 font-semibold">{type}</code>
      </div>
      <div className="flex items-start justify-between gap-2 text-sm">
        <span className="text-amber-700 dark:text-amber-400 font-medium shrink-0">{t('domain.byod.nameHost')}:</span>
        <div className="min-w-0">
          {showFullName ? <CopyableValue value={fullName!} t={t} /> : nameDisplay}
          {fullName && (
            <button
              onClick={() => setShowFullName(!showFullName)}
              className="block text-[10px] text-amber-600 dark:text-amber-500 hover:underline mt-0.5"
            >
              {showFullName ? t('domain.byod.showShortValue') : t('domain.byod.showFullValue')}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-start justify-between gap-2 text-sm">
        <span className="text-amber-700 dark:text-amber-400 font-medium shrink-0">{t('domain.byod.value')}:</span>
        <CopyableValue value={value} t={t} />
      </div>
    </div>
  )
}

interface DnsManagementSectionProps {
  project: Project
}

function buildDnsInstructionsText(project: Project): string {
  const dp = project.domain_purchase!
  const isWwwDomain = project.django_stack && project.custom_domain?.startsWith('www.')
  const apexDomain = project.custom_domain?.replace(/^www\./, '')
  const lines: string[] = [
    `DNS Setup Instructions for ${project.custom_domain}`,
    `${'='.repeat(50)}`,
    '',
  ]

  if (dp.cname_target) {
    lines.push('DOMAIN POINTING RECORD')
    lines.push('-'.repeat(30))
    if (dp.is_apex_domain) {
      lines.push(`Record Type: ALIAS / ANAME`)
      lines.push(`Name/Host:   @`)
    } else {
      lines.push(`Record Type: CNAME`)
      lines.push(`Name/Host:   ${dp.subdomain_label || project.custom_domain}`)
    }
    lines.push(`Value:       ${dp.cname_target}`)
    lines.push('')

    if (isWwwDomain && apexDomain) {
      lines.push(`APEX DOMAIN RECORD`)
      lines.push('-'.repeat(30))
      lines.push(`Point your apex domain to the same CloudFront distribution.`)
      lines.push(`Visitors to ${apexDomain} will be automatically redirected to ${project.custom_domain}.`)
      lines.push('')
      lines.push(`Record Type: ALIAS / ANAME`)
      lines.push(`Name/Host:   @`)
      lines.push(`Value:       ${dp.cname_target}`)
      lines.push('')
    }
  }

  const records = dp.acm_validation_records?.length
    ? dp.acm_validation_records
    : dp.acm_validation_cname_name
      ? [{ domain: project.custom_domain || '', name: dp.acm_validation_cname_name, value: dp.acm_validation_cname_value || '' }]
      : []

  if (records.length > 0) {
    lines.push('SSL CERTIFICATE VALIDATION RECORDS')
    lines.push('-'.repeat(30))
    lines.push('Note: Most DNS providers automatically append your domain name.')
    lines.push('')
    records.forEach((r) => {
      if (records.length > 1) lines.push(`For ${r.domain}:`)
      lines.push(`Record Type: CNAME`)
      lines.push(`Name/Host:   ${stripDomainSuffix(r.name, r.domain)}`)
      lines.push(`Value:       ${r.value}`)
      lines.push('')
    })
  }

  return lines.join('\n')
}

export function DnsManagementSection({ project }: DnsManagementSectionProps) {
  const { t } = useTranslation('conversations')
  const [showBuyDialog, setShowBuyDialog] = useState(false)
  const [showByodDialog, setShowByodDialog] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const checkFiredRef = useRef(false)
  const isWwwDomain = project.django_stack && project.custom_domain?.startsWith('www.')
  const apexDomain = project.custom_domain?.replace(/^www\./, '')

  // On-demand check: fire once on mount when domain is in a pending state.
  // The sweep job checks every minute in the background, but this gives
  // immediate feedback when the user opens the page. WebSocket delivers the update.
  useEffect(() => {
    if (checkFiredRef.current) return
    if (project.custom_domain_status === 'not_activated' ||
        project.custom_domain_status === 'activating_certificate') {
      checkFiredRef.current = true
      fetch(`/projects/${project.id}/custom_domain/check_status`)
        .catch(() => {}) // fire-and-forget
    }
  }, [project.id, project.custom_domain_status])

  const getStatusBadge = () => {
    switch (project.custom_domain_status) {
      case 'not_activated':
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
            <Clock className="h-3 w-3 mr-1" />
            {t('domain.statusSettingUp')}
          </Badge>
        )
      case 'configuring_dns':
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            {t('domain.statusConfiguringDns')}
          </Badge>
        )
      case 'activating_certificate':
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            {t('domain.statusActivatingSsl')}
          </Badge>
        )
      case 'active':
        return (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('domain.statusActive')}
          </Badge>
        )
      case 'failed':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('domain.statusFailed')}
          </Badge>
        )
      case 'stale':
        return (
          <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t('domain.statusStale')}
          </Badge>
        )
      default:
        return null
    }
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('domain.title')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {t('domain.description')}
        </p>
      </div>

      {!project.custom_domain ? (
        // No domain configured
        <div className="border border-dashed rounded-lg p-8 text-center bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
            <Globe className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {t('domain.noDomain')}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
            {t('domain.noDomainDescription')}
          </p>
          {project.can_purchase_domain ? (
            <div className="flex gap-2 justify-center">
              <Button onClick={() => setShowBuyDialog(true)} size="sm" variant="default">
                <Globe className="h-4 w-4 mr-2" />
                {t('domain.buyDomain')}
              </Button>
              <Button onClick={() => setShowByodDialog(true)} size="sm" variant="outline">
                <Globe className="h-4 w-4 mr-2" />
                {t('domain.bringYourOwn')}
              </Button>
            </div>
          ) : (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-left inline-block">
              <p className="text-xs font-medium text-amber-900 dark:text-amber-300 mb-1">
                {t('domain.cannotConfigure')}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {t('domain.deployFirst')}
              </p>
            </div>
          )}
        </div>
      ) : (
        // Domain configured
        <div className="space-y-4">
          {/* Domain Status Card */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {project.custom_domain}
                  </h4>
                  {getStatusBadge()}
                </div>

                {project.custom_domain_status === 'active' && (
                  <a
                    href={`https://${project.custom_domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    {t('domain.visitDomain')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}

                {project.custom_domain_activated_at && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {t('domain.activated')} {new Date(project.custom_domain_activated_at).toLocaleString()}
                  </p>
                )}

                {project.custom_domain_expires_at && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('domain.expires')} {new Date(project.custom_domain_expires_at).toLocaleString()}
                  </p>
                )}

                {project.custom_domain_error && (
                  <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    <p className="text-xs font-medium text-red-900 dark:text-red-300 mb-1">
                      {t('domain.configurationError')}
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-400 break-words">
                      {project.custom_domain_error}
                    </p>
                  </div>
                )}

                {(project.custom_domain_status === 'not_activated' ||
                  project.custom_domain_status === 'configuring_dns' ||
                  project.custom_domain_status === 'activating_certificate') && (
                  <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <p className="text-xs text-blue-800 dark:text-blue-300">
                      {project.custom_domain_status === 'not_activated'
                        ? project.domain_purchase?.registration_type === 'byod'
                          ? t('domain.statusMessages.byodAutoCheck')
                          : t('domain.statusMessages.domainSetupStarting')
                        : project.custom_domain_status === 'configuring_dns'
                        ? t('domain.statusMessages.configuringDns')
                        : t('domain.statusMessages.activatingCertificate')}
                    </p>
                    {project.domain_purchase?.sweep_started_at && project.domain_purchase?.registration_type === 'byod' && (() => {
                      const elapsed = Math.floor((Date.now() - new Date(project.domain_purchase!.sweep_started_at!).getTime()) / (1000 * 60 * 60 * 24))
                      const daysLeft = Math.max(0, 7 - elapsed)
                      return (
                        <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
                          {t('domain.byod.daysRemaining', { days: daysLeft })}
                        </p>
                      )
                    })()}
                  </div>
                )}

                {project.custom_domain_status === 'stale' && (
                  <div className="mt-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                    <p className="text-xs text-orange-800 dark:text-orange-300">
                      {t('domain.statusMessages.staleExpired')}
                    </p>
                  </div>
                )}

                {/* DNS records for BYOD domains — always available, collapsed when active */}
                {project.domain_purchase?.registration_type === 'byod' && (
                  <details
                    className="mt-3"
                    open={project.custom_domain_status === 'not_activated' || project.custom_domain_status === 'activating_certificate'}
                  >
                    <summary className="text-xs font-medium text-amber-700 dark:text-amber-400 cursor-pointer select-none flex items-center justify-between">
                      <span>{t('domain.byod.dnsRecordsTitle')}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-amber-600 dark:text-amber-400 hover:text-amber-800"
                        onClick={(e) => {
                          e.preventDefault()
                          const content = buildDnsInstructionsText(project)
                          const blob = new Blob([content], { type: 'text/plain' })
                          const url = URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = `dns-setup-${project.custom_domain}.txt`
                          a.click()
                          setTimeout(() => URL.revokeObjectURL(url), 100)
                        }}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        {t('domain.byod.downloadInstructions')}
                      </Button>
                    </summary>
                  <div className="mt-2 space-y-3">
                    {/* Domain pointing record */}
                    {project.domain_purchase.cname_target && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                        <p className="text-xs font-medium text-amber-900 dark:text-amber-300 mb-2">
                          {t('domain.byod.domainPointingTitle')}
                        </p>
                        {project.domain_purchase.is_apex_domain ? (
                          <div className="space-y-1.5">
                            <p className="text-[10px] text-amber-700 dark:text-amber-400 mb-2">
                              {t('domain.byod.apexAliasHint')}
                            </p>
                            {project.domain_purchase.ipv4_addresses && project.domain_purchase.ipv4_addresses.length > 0 ? (
                              project.domain_purchase.ipv4_addresses.map((ip, index) => (
                                <DnsRecordRow key={index} type="A" name="@" value={ip} t={t} />
                              ))
                            ) : (
                              <DnsRecordRow type="ALIAS / ANAME" name="@" value={project.domain_purchase.cname_target} t={t} />
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <p className="text-[10px] text-amber-700 dark:text-amber-400 mb-2">
                              {t('domain.byod.subdomainCnameHint')}
                            </p>
                            <DnsRecordRow type="CNAME" name={project.domain_purchase.subdomain_label || project.custom_domain || ''} value={project.domain_purchase.cname_target} copyName t={t} />
                            {isWwwDomain && apexDomain && (
                              <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                                <p className="text-xs font-medium text-amber-900 dark:text-amber-300 mb-1">
                                  {t('domain.byod.apexRecordTitle')}
                                </p>
                                <p className="text-[10px] text-amber-700 dark:text-amber-400 mb-2">
                                  {t('domain.byod.apexRecordHint')}
                                </p>
                                <DnsRecordRow type="ALIAS / ANAME" name="@" value={project.domain_purchase.cname_target} copyName t={t} />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {(project.domain_purchase?.acm_validation_records?.length || project.domain_purchase?.acm_validation_cname_name) && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                        <p className="text-xs font-medium text-amber-900 dark:text-amber-300 mb-1">
                          {t('domain.byod.sslValidationTitle')}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-400 mb-1">
                          {t('domain.byod.sslValidationHint')}
                        </p>
                        <p className="text-[10px] text-amber-600 dark:text-amber-500 mb-3">
                          {t('domain.byod.dnsAutoAppendHint')}
                        </p>
                        <div className="space-y-3">
                          {(project.domain_purchase.acm_validation_records?.length ? project.domain_purchase.acm_validation_records : [
                            { domain: project.custom_domain || '', name: project.domain_purchase.acm_validation_cname_name || '', value: project.domain_purchase.acm_validation_cname_value || '' }
                          ]).map((record, index) => (
                            <div key={index}>
                              {project.domain_purchase!.acm_validation_records && project.domain_purchase!.acm_validation_records.length > 1 && (
                                <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400 mb-1.5">
                                  {t('domain.byod.validationFor', { domain: record.domain })}
                                </p>
                              )}
                              <DnsRecordRow type="CNAME" name={stripDomainSuffix(record.name, record.domain)} value={record.value} copyName fullName={record.name} t={t} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  </details>
                )}
              {/* Remove domain button — at bottom */}
              {project.domain_purchase?.registration_type === 'byod' && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRemoveDialog(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
                  >
                    {t('domain.removeDomain')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Buy Domain Dialog */}
      <BuyDomainDialog
        open={showBuyDialog}
        onOpenChange={setShowBuyDialog}
        projectId={project.id}
      />

      {/* Add BYOD Dialog */}
      <AddByodDialog
        open={showByodDialog}
        onOpenChange={setShowByodDialog}
        projectId={project.id}
      />

      {/* Remove Domain Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('domain.removeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                {t('domain.removeDialog.aboutToRemove', { domain: project.custom_domain })}
              </p>
              <p className="text-sm">
                {t('domain.removeDialog.willDo')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('domain.removeDialog.removeConfig')}</li>
                <li>{t('domain.removeDialog.deactivateSsl')}</li>
                <li>{t('domain.removeDialog.disconnectDomain')}</li>
              </ul>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
                  {t('domain.removeDialog.dnsWarningTitle')}
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-400">
                  {t('domain.removeDialog.dnsWarningDescription')}
                </p>
              </div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {t('domain.removeDialog.cannotUndo')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRemoving}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={async (e) => {
                e.preventDefault()
                setIsRemoving(true)

                try {
                  const response = await fetch(`/projects/${project.id}/custom_domain`, {
                    method: 'DELETE',
                    headers: csrfHeaders(),
                  })

                  const data = await response.json()

                  if (response.ok && data.success) {
                    toast.success(t('domain.toasts.domainRemovedSuccess'))
                    setShowRemoveDialog(false)
                    router.reload({ only: ['project'] })
                  } else {
                    const errorMsg = data.errors?.join(', ') || data.error || t('domain.toasts.domainRemoveError')
                    toast.error(errorMsg)
                  }
                } catch (error) {
                  console.error('Remove domain error:', error)
                  toast.error(t('domain.toasts.networkErrorRemove'))
                } finally {
                  setIsRemoving(false)
                }
              }}
            >
              {isRemoving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('domain.removeDialog.removingText')}
                </>
              ) : (
                t('domain.removeDomain')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
